import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
} from 'reactstrap';

/**
 * Detalle de un insight automático.
 *
 * No hace ninguna petición: la lista ya trae `metrics` completo, que es lo que
 * sustenta la cifra del texto. Abrir una fila no debería costar un viaje al
 * servidor para mostrar algo que ya está en memoria.
 */

const TYPE_LABEL = {
  NETWORK_CONCENTRATION: 'Concentración de ventas',
  PARETO_8020: 'Usuarios de alto valor',
  REGION_SALES_VARIATION: 'Oportunidad detectada',
};

const money = (value) =>
  new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(
    Math.round(Number(value) || 0),
  );

const dateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('es-CO');
};

/**
 * Filas de respaldo del insight, según su tipo.
 *
 * Cada detector guarda cosas distintas en `metrics`, así que no hay una tabla
 * genérica que sirva para los tres: se arma la que corresponde y, si el tipo no
 * está contemplado, se cae al volcado crudo en vez de no mostrar nada.
 */
const buildRows = (insight) => {
  const metrics = insight?.metrics || {};

  if (insight?.type === 'NETWORK_CONCENTRATION') {
    return [
      ['Código', metrics.code ?? '—'],
      ['Órdenes aprobadas de su estructura', metrics.approved_orders ?? '—'],
      ['Órdenes aprobadas de la red', metrics.network_approved_orders ?? '—'],
      ['Participación', `${metrics.share_percentage ?? '—'}%`],
      ['Volumen de su estructura', money(metrics.structure_total)],
      ['Usuarios en su estructura', metrics.structure_size ?? '—'],
      ['Directos', metrics.direct_children ?? '—'],
      ['Códigos con órdenes', metrics.codes_with_orders ?? '—'],
      ['Días del período', metrics.period_days ?? '—'],
    ];
  }

  if (insight?.type === 'PARETO_8020') {
    const top = metrics.top_line || {};
    return [
      ['Línea con mayor participación', top.code || top.name || '—'],
      ['Participación', `${top.participation_percentage ?? '—'}%`],
      ['Puntos de su estructura', money(top.points)],
      ['Puntos de todas las líneas', money(metrics.lines_total)],
      ['Usuarios en su estructura', top.structure_size ?? '—'],
      ['Activos en su estructura', top.active_members ?? '—'],
      ['Líneas comparadas', metrics.total_lines ?? '—'],
      ['Directos excluidos', metrics.excluded_lines ?? '—'],
      [
        'Volumen fuera de estas líneas',
        `${metrics.outside_lines_percentage ?? 0}%`,
      ],
      ['Días del período', metrics.period_days ?? '—'],
    ];
  }

  if (insight?.type === 'REGION_SALES_VARIATION') {
    const region = metrics.variations?.[0];
    if (!region) return null;

    return [
      ['Región', region.province_label ?? '—'],
      ['Período actual', metrics.period_label ?? '—'],
      ['Período anterior', metrics.previous_period_label ?? '—'],
      ['Ventas actuales', money(region.current_total)],
      ['Ventas anteriores', money(region.previous_total)],
      ['Órdenes', `${region.orders_previous ?? 0} → ${region.orders_current ?? 0}`],
      ['Variación', `${region.variation_pct ?? '—'}%`],
      ['Regiones analizadas', metrics.total_regions ?? '—'],
      [
        'Ventas sin región',
        `${money(metrics.unattributed_total)} (${metrics.unattributed_share_pct ?? 0}%)`,
      ],
    ];
  }

  return null;
};

const InsightDetailModal = ({ isOpen, toggle, insight }) => {
  if (!insight) return null;

  const rows = buildRows(insight);
  const region = insight.metrics?.variations;
  const lines = insight.metrics?.lines;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
      <ModalHeader toggle={toggle}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#101828' }}>
            {TYPE_LABEL[insight.type] || insight.type}
          </div>
          <small style={{ color: '#98A2B3', fontWeight: 400 }}>
            Generado el {dateLabel(insight.generated_at)}
          </small>
        </div>
      </ModalHeader>

      <ModalBody>
        <p style={{ fontSize: 14, color: '#344054', marginBottom: 4 }}>
          {insight.text}
        </p>

        {insight.title && (
          <Badge color="light" className="text-dark border mb-3">
            {insight.title}
          </Badge>
        )}

        <h6 className="fw-bold mt-3 mb-2" style={{ color: '#101828' }}>
          Cifras que lo sustentan
        </h6>

        {rows ? (
          <Table responsive size="sm" className="mb-0">
            <tbody>
              {rows.map(([label, value]) => (
                <tr key={label}>
                  <td style={{ color: '#667085', width: '45%' }}>{label}</td>
                  <td style={{ fontWeight: 600, color: '#344054' }}>{value}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          // Sin tabla específica para el tipo: se muestra el metrics crudo,
          // que siempre es mejor que una tarjeta vacía.
          <pre
            style={{
              background: '#F8F9FC',
              padding: 12,
              borderRadius: 6,
              fontSize: 11,
              maxHeight: 260,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(insight.metrics ?? {}, null, 2)}
          </pre>
        )}

        {/*
          El titular es la línea más alta, pero lo que se pidió ver es el
          reparto completo: cada directo con su porcentaje sobre el 100%.
        */}
        {Array.isArray(lines) && lines.length > 1 && (
          <>
            <h6 className="fw-bold mt-4 mb-2" style={{ color: '#101828' }}>
              Participación de cada línea
            </h6>

            <Table responsive size="sm" hover className="mb-0">
              <thead>
                <tr style={{ fontSize: 11, color: '#98A2B3' }}>
                  <th>Directo</th>
                  <th className="text-end">Estructura</th>
                  <th className="text-end">Puntos</th>
                  <th className="text-end">Participación</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((row) => (
                  <tr key={row.user_id} style={{ fontSize: 12 }}>
                    <td>
                      {row.name || '—'}
                      {row.code && (
                        <span style={{ color: '#98A2B3' }}> ({row.code})</span>
                      )}
                    </td>
                    <td className="text-end">{row.structure_size ?? 0}</td>
                    <td className="text-end">{money(row.points)}</td>
                    <td className="text-end fw-bold" style={{ color: '#12B76A' }}>
                      {row.participation_percentage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {/*
          El insight titula con una sola región, pero el detector evaluó todas.
          Verlas juntas es lo que permite notar que la del titular ganó por
          porcentaje y no por volumen.
        */}
        {Array.isArray(region) && region.length > 1 && (
          <>
            <h6 className="fw-bold mt-4 mb-2" style={{ color: '#101828' }}>
              Todas las regiones evaluadas
            </h6>

            <Table responsive size="sm" hover className="mb-0">
              <thead>
                <tr style={{ fontSize: 11, color: '#98A2B3' }}>
                  <th>Región</th>
                  <th className="text-end">Anterior</th>
                  <th className="text-end">Actual</th>
                  <th className="text-end">Variación</th>
                </tr>
              </thead>
              <tbody>
                {region.map((row) => (
                  <tr key={row.province_key} style={{ fontSize: 12 }}>
                    <td>{row.province_label}</td>
                    <td className="text-end">{money(row.previous_total)}</td>
                    <td className="text-end">{money(row.current_total)}</td>
                    <td
                      className="text-end fw-bold"
                      style={{
                        color: row.direction === 'DOWN' ? '#EA5455' : '#12B76A',
                      }}
                    >
                      {row.variation_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InsightDetailModal;
